import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from '@serota/icons/web';
import { cx } from '../lib';

export type ButtonVariant = 'primary' | 'secondary' | 'dark' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'md' | 'sm' | 'driver';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  icon?: IconName;
  iconRight?: IconName;
  children?: ReactNode;
}

export type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

function classes({ variant = 'secondary', size = 'md', block, loading }: CommonProps, extra?: string) {
  return cx(
    'sr-btn',
    `sr-btn--${variant}`,
    size === 'sm' && 'sr-btn--sm',
    size === 'driver' && 'sr-btn--driver',
    block && 'sr-btn--block',
    loading && 'is-loading',
    extra,
  );
}

/**
 * Buton. Kural: ekran başına en fazla bir `primary` (turuncu); turuncu üzerindeki metin `on-brand`.
 * Yıkıcı işlemler (`danger`) her zaman onay dialog'u ile kullanılır.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, block, loading, icon, iconRight, children, className, type = 'button', disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={classes({ variant, size, block, loading }, className)}
      disabled={disabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {icon ? <Icon name={icon} size={size === 'driver' ? 'lg' : 'md'} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} /> : null}
    </button>
  );
});

export type LinkButtonProps = CommonProps & AnchorHTMLAttributes<HTMLAnchorElement>;

export function LinkButton({ variant, size, block, icon, iconRight, children, className, ...rest }: LinkButtonProps) {
  return (
    <a className={classes({ variant, size, block }, className)} {...rest}>
      {icon ? <Icon name={icon} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} /> : null}
    </a>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  label: string;
  size?: 'md' | 'sm';
  ghost?: boolean;
  badge?: number;
}

/** 44px (tap-min) ikon butonu; erişilebilir etiket zorunlu. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 'md', ghost, badge, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx('sr-iconbtn', size === 'sm' && 'sr-iconbtn--sm', ghost && 'sr-iconbtn--ghost', className)}
      style={badge ? { position: 'relative' } : undefined}
      {...rest}
    >
      <Icon name={icon} />
      {badge ? (
        <span className="sr-side__count" style={{ position: 'absolute', top: -4, right: -4, margin: 0 }}>
          {badge}
        </span>
      ) : null}
    </button>
  );
});
